/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in component trees and prevents the entire app from crashing.
 * Logs errors to Logfire for monitoring and provides a fallback UI.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */

import React, { Component, ReactNode, ErrorInfo } from "react";
import { logger } from "../lib/logger";

interface Props {
  children: ReactNode;
  /**
   * Optional custom fallback UI to show when an error occurs
   * If not provided, uses default error UI
   */
  fallback?: ReactNode;
  /**
   * Optional name for this boundary (used in logging)
   */
  name?: string;
  /**
   * Optional callback when error occurs
   */
  onError?: (_error: Error, _errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Logfire with full context
    const boundaryName = this.props.name || "ErrorBoundary";
    // The backend already uses `surface` to distinguish cloud and Excel telemetry. Keep public
    // participant crashes queryable without adding the capability-bearing slug as an error field.
    const surface = /^\/interview\/[^/]+\/?$/.test(window.location.pathname)
      ? "public_interview"
      : undefined;

    logger.error("React component error caught by boundary", error, {
      boundary: boundaryName,
      componentStack: errorInfo.componentStack,
      errorMessage: error.message,
      errorName: error.name,
      surface,
    });

    // Store error info for display
    this.setState({ errorInfo });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div
          style={{
            padding: "20px",
            margin: "20px",
            border: "1px solid #ff6b6b",
            borderRadius: "4px",
            backgroundColor: "#ffe0e0",
            color: "#333",
          }}
        >
          <h2 style={{ margin: "0 0 10px 0", color: "#d32f2f" }}>Something went wrong</h2>
          <p style={{ margin: "0 0 10px 0" }}>
            An error occurred in the application. The error has been logged for investigation.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <details style={{ marginTop: "10px" }}>
              <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "5px" }}>
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  padding: "10px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                  overflow: "auto",
                  fontSize: "12px",
                }}
              >
                {this.state.error.toString()}
                {"\n\n"}
                {this.state.error.stack}
                {this.state.errorInfo?.componentStack &&
                  "\n\nComponent Stack:" + this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}

          <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: "8px 16px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Try Again
            </button>
            <button
              onClick={this.handleReload}
              style={{
                padding: "8px 16px",
                backgroundColor: "#424242",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary wrapper
 * For use in functional components
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  name?: string
): React.FC<P> {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary name={name || Component.displayName || Component.name}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
